import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiDownload, FiRotateCcw } from "react-icons/fi";

import { fetchRecentlyPlayed } from "../../redux/actions/settingsActions";
import { loadOfflineCatalog, startOfflineDownload, useOfflineGames } from "../../services/offlineGames/offlineGamesStore";
import { isOfflinePlaySupported } from "../../services/offlineGames/serviceWorker";
import useOnlineStatus from "../../services/offlineGames/useOnlineStatus";

// Whether the game details page offers the offline Download button (AK-255):
// a logged-in user who already played the game, a game that can be played
// offline, and not downloaded yet. Downloads live in the offline store, so they
// keep going when the user leaves the page and show up again on return.
export const useGameDownload = (appId) => {
	const dispatch = useDispatch();
	const isOnline = useOnlineStatus();
	const { user, token } = useSelector((state) => state.Auth);
	const { recentlyPlayedData } = useSelector((state) => state.Settings);
	const { catalog, downloads, downloaded } = useOfflineGames();
	const isLoggedIn = Boolean(user && token);
	const supported = isLoggedIn && isOnline && isOfflinePlaySupported();

	useEffect(() => {
		if (!supported) return;
		loadOfflineCatalog().catch(() => {});
		dispatch(fetchRecentlyPlayed());
	}, [supported, dispatch]);

	const played = (recentlyPlayedData?.items || []).some((game) => String(game?.id) === String(appId));
	const download = downloads[appId];
	return {
		// An active download stays visible even if the lists are still reloading.
		show: Boolean(download) || (supported && played && Boolean(catalog?.[appId]) && !downloaded[appId]),
		status: download?.status || "idle",
		progress: Math.round((download?.progress || 0) * 100),
		start: () => startOfflineDownload(appId),
	};
};

// Icon-only outlined pill beside Play; shows the download's progress, or Retry.
const GameDownloadButton = ({ download, className = "" }) => {
	const { status, progress, start } = download;
	const isDownloading = status === "downloading";
	const label = isDownloading ? `Downloading for offline play, ${progress}%` : status === "failed" ? "Download failed. Retry" : "Download for offline play";

	return (
		<button
			type="button"
			className={`gameDownloadBtn ${isDownloading ? "isDownloading" : ""} ${status === "failed" ? "isFailed" : ""} ${className}`}
			onClick={isDownloading ? undefined : start}
			aria-label={label}
			title={label}
			aria-busy={isDownloading}
		>
			{isDownloading && <span className="gameDownloadFill" style={{ width: `${progress}%` }} aria-hidden="true" />}
			<span className="gameDownloadContent" aria-hidden="true">
				{isDownloading ? `${progress}%` : status === "failed" ? <FiRotateCcw size={18} /> : <FiDownload size={18} />}
			</span>
		</button>
	);
};

export default GameDownloadButton;
